import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Image,
  Link,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Spinner,
  Text,
  VisuallyHidden,
  useBreakpointValue,
  useToast,
} from '@chakra-ui/react';
import { Avatar } from '../Avatar';
import {
  Ban,
  Check,
  CheckCheck,
  Forward,
  Lock,
  MoreHorizontal,
  Pin,
  Play,
  Star,
  Timer,
} from 'lucide-react';
import { expiryLabel } from '../../lib/disappearing';
import { Fragment, useMemo, useState } from 'react';
import { messageActionItems } from './messageActionItems';
import {
  EVERYONE_MENTION,
  splitMentions,
  type GatheringReply,
} from '@twinden/shared';
import { QuickReactions } from './QuickReactions';
import { canBless } from './blessing';
import { blessingButtonAtom } from '../../store';
import { bigEmojiSize, canonicalEmoji, EMOJI_FONT } from '../../lib/emoji';
import { LinkPreviewCard } from './LinkPreviewCard';
import { shortenLinks, splitLinks } from '../../lib/shortLinks';
import type { LinkPreview } from '../../lib/api';
import { VoiceNotePlayer } from './VoiceNotePlayer';
import { FileCard } from './FileCard';
import { AlbumGrid } from './AlbumGrid';
import { PollCard, type PollActions } from './PollCard';
import { GatheringCard, type GatheringActions } from './GatheringCard';
import { mediaSecrets, useMediaUrl } from '../../lib/e2ee/media';
import {
  firstUrl,
  formatTime,
  isKnownType,
  shownSenderName,
  type ChatMessage,
  type MessageMetadata,
} from './messageUtils';
import { appLocale } from '../../lib/format';
import { useAtomValue } from 'jotai';
import { memberLabelsAtom, memberNameAtom } from '../../store';
import { useT } from '../../i18n';
import { isUnbound, UnboundMark } from '../UnboundMark';

const MEDIA_MAX = 320;

// Display size for media with known dimensions, so the bubble reserves its
// space before the file loads and the list does not jump under the reader.
function mediaBox(meta: MessageMetadata) {
  if (!meta.width || !meta.height) return null;
  const scale = Math.min(1, MEDIA_MAX / meta.width, MEDIA_MAX / meta.height);
  return {
    width: Math.round(meta.width * scale),
    aspectRatio: `${meta.width} / ${meta.height}`,
  };
}

interface MessageBodyProps {
  message: ChatMessage;
  isOwn: boolean;
  /** Handles this message mentions (server-validated, D27). */
  mentionHandles: ReadonlySet<string>;
  myHandle: string | undefined;
  /** `index` is the item tapped in an album (M60). */
  onOpenImage: (message: ChatMessage, index?: number) => void;
  onMediaLoad: () => void;
  currentUserId: string | undefined;
  pollActions: PollActions;
  gatheringActions: GatheringActions;
  /** Everyone in the chat, and whether you may ring as a family admin. */
  gatheringPeople: { ids: string[]; isAdmin: boolean };
  /** 1–3 emojis and nothing else: their font size, drawn big (D265). */
  bigEmoji?: number | null;
}

// Encrypted media (M28) is decrypted on the device before it shows; plain
// files pass straight through.
function SealedImage({
  message,
  meta,
  box,
  onOpen,
  onLoad,
}: {
  message: ChatMessage;
  meta: MessageMetadata;
  box: ReturnType<typeof mediaBox>;
  onOpen: () => void;
  onLoad: () => void;
}) {
  const t = useT();
  const secrets = mediaSecrets(message);
  // The small preview stored with the upload (#2); the lightbox fetches the
  // full image only when opened.
  const preview = useMediaUrl(
    meta.thumbnailUrl ?? message.content,
    meta.thumbnailUrl ? secrets.thumb : secrets.file,
    meta.thumbnailUrl ? 'image/jpeg' : secrets.mimeType
  );
  return (
    <Box
      w={box ? `${box.width}px` : undefined}
      maxW={box ? '100%' : `min(${MEDIA_MAX}px, 100%)`}
      sx={box ? { aspectRatio: box.aspectRatio } : undefined}
      bg={preview.src ? undefined : 'surface.subtle'}
      borderRadius="20px"
      minH={preview.src ? undefined : '120px'}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      {preview.src ? (
        <Image
          src={preview.src}
          alt={meta.fileName ?? t('chat.message.imageAlt')}
          w="full"
          maxH={box ? undefined : `${MEDIA_MAX}px`}
          h="auto"
          objectFit="cover"
          borderRadius="20px"
          cursor="pointer"
          onClick={onOpen}
          onLoad={onLoad}
        />
      ) : preview.failed ? (
        <Text fontSize="14px" color="muted">
          {t('chat.message.photoFailed')}
        </Text>
      ) : (
        <Spinner color="accent" />
      )}
    </Box>
  );
}

// The preview shows at once with a play button; the video itself is fetched
// (and decrypted) only when someone taps play. Waiting for the whole file
// before showing anything left phones on a spinner where the preview should
// be (2026-10-22), and downloaded every video in a chat just to scroll past.
function SealedVideo({
  message,
  meta,
  box,
  onLoad,
}: {
  message: ChatMessage;
  meta: MessageMetadata;
  box: ReturnType<typeof mediaBox>;
  onLoad: () => void;
}) {
  const t = useT();
  const secrets = mediaSecrets(message);
  const [wanted, setWanted] = useState(false);
  const poster = useMediaUrl(meta.thumbnailUrl, secrets.thumb, 'image/jpeg');
  const video = useMediaUrl(
    wanted ? message.content : undefined,
    secrets.file,
    secrets.mimeType
  );
  const style = {
    width: box ? `${box.width}px` : undefined,
    maxWidth: box ? '100%' : `min(${MEDIA_MAX}px, 100%)`,
    aspectRatio: box?.aspectRatio,
    borderRadius: 20,
    display: 'block',
  };
  if (wanted && video.src) {
    return (
      <video
        src={video.src}
        poster={poster.src}
        controls
        autoPlay
        playsInline
        preload="auto"
        style={style}
        onLoadedMetadata={onLoad}
      />
    );
  }
  return (
    <Box
      as="button"
      type="button"
      aria-label={t(
        wanted ? 'chat.message.loadingVideo' : 'chat.message.playVideo'
      )}
      onClick={() => setWanted(true)}
      style={style}
      position="relative"
      overflow="hidden"
      minH="120px"
      w={box ? undefined : `min(${MEDIA_MAX}px, 100%)`}
      bg="surface.subtle"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      {poster.src && (
        <Image
          src={poster.src}
          alt=""
          position="absolute"
          inset={0}
          w="100%"
          h="100%"
          objectFit="cover"
          onLoad={onLoad}
        />
      )}
      {wanted && video.failed ? (
        <Text
          position="relative"
          fontSize="14px"
          color="white"
          bg="blackAlpha.600"
          px={3}
          py={1}
          borderRadius="full"
        >
          {t('chat.message.videoFailed')}
        </Text>
      ) : (
        <Flex
          position="relative"
          w="56px"
          h="56px"
          borderRadius="full"
          bg="blackAlpha.600"
          color="white"
          align="center"
          justify="center"
        >
          {wanted ? (
            <Spinner />
          ) : (
            <Play size={24} fill="currentColor" style={{ marginLeft: 3 }} />
          )}
        </Flex>
      )}
      {meta.duration ? (
        <Text
          position="absolute"
          left="10px"
          bottom="8px"
          fontSize="13px"
          fontWeight={600}
          color="white"
          bg="blackAlpha.600"
          px={2}
          borderRadius="full"
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {formatTime(meta.duration)}
        </Text>
      ) : null}
    </Box>
  );
}

function SealedVoice({
  message,
  meta,
  isOwn,
}: {
  message: ChatMessage;
  meta: MessageMetadata;
  isOwn: boolean;
}) {
  const t = useT();
  const secrets = mediaSecrets(message);
  const audio = useMediaUrl(message.content, secrets.file, secrets.mimeType);
  if (!audio.src) {
    return (
      <HStack h="52px" spacing={2} color="muted" fontSize="14px">
        {audio.failed ? (
          <Text>{t('chat.message.voiceFailed')}</Text>
        ) : (
          <Spinner size="sm" color="accent" />
        )}
      </HStack>
    );
  }
  return (
    <VoiceNotePlayer
      id={message.id}
      src={audio.src}
      duration={meta.duration}
      waveform={meta.waveform}
      isOwn={isOwn}
    />
  );
}

function MessageBody({
  message,
  isOwn,
  mentionHandles,
  myHandle,
  onOpenImage,
  onMediaLoad,
  currentUserId,
  pollActions,
  gatheringActions,
  gatheringPeople,
  bigEmoji,
}: MessageBodyProps) {
  const t = useT();
  const meta = (message.metadata ?? {}) as MessageMetadata;
  const box = mediaBox(meta);

  const isForMe = (handle: string) =>
    handle === myHandle || (handle === EVERYONE_MENTION && !isOwn);

  // No key on this device yet: another device will share it (D57). The
  // chat's banner explains why (D137); each message just says it's locked.
  // Or the key is here but it isn't what it claims to be (M76, D327):
  // never shown, and never as anyone's words.
  if (message.e2ee?.locked) {
    return (
      <HStack spacing={2} color="muted" fontStyle="italic" fontSize="15px">
        <Lock size={15} aria-hidden="true" />
        <Text>
          {t(
            // Not what it claims (D327), or plain text where only sealed text can be (D347).
            message.e2ee.unverified
              ? 'chat.message.unverified'
              : 'chat.message.locked'
          )}
        </Text>
      </HStack>
    );
  }

  switch (message.type) {
    case 'image':
    case 'gif':
      return (
        <SealedImage
          message={message}
          meta={meta}
          box={box}
          onOpen={() => onOpenImage(message)}
          onLoad={onMediaLoad}
        />
      );

    case 'video':
      return (
        <SealedVideo
          message={message}
          meta={meta}
          box={box}
          onLoad={onMediaLoad}
        />
      );

    case 'voice':
      return <SealedVoice message={message} meta={meta} isOwn={isOwn} />;

    // M60 (D219): the collage, then its caption.
    case 'album':
      return (
        <Box>
          <AlbumGrid
            message={message}
            onOpen={(index) => onOpenImage(message, index)}
            onLoad={onMediaLoad}
          />
          {message.content && (
            <Text pt={2} whiteSpace="pre-wrap" overflowWrap="anywhere">
              {message.content}
            </Text>
          )}
        </Box>
      );

    // M60 (D220): votes are metadata; the words were opened on the device.
    case 'poll':
      return (
        <PollCard
          message={message}
          currentUserId={currentUserId}
          isOwn={isOwn}
          actions={pollActions}
        />
      );

    // M71 (D282): replies are metadata; the words were opened on the device.
    case 'gathering':
      return (
        <GatheringCard
          message={message}
          currentUserId={currentUserId}
          participantIds={gatheringPeople.ids}
          isAdmin={gatheringPeople.isAdmin}
          actions={gatheringActions}
        />
      );

    case 'file':
      return (
        <FileCard
          url={message.content}
          secret={mediaSecrets(message).file}
          mimeType={mediaSecrets(message).mimeType}
          fileName={meta.fileName ?? t('chat.message.attachment')}
          fileSize={meta.fileSize}
        />
      );

    default: {
      // A type from a newer Twinden: never its raw content (D222).
      if (!isKnownType(message.type)) {
        return (
          <Text fontStyle="italic" color="muted" fontSize="15px">
            {t('chat.message.updateApp')}
          </Text>
        );
      }
      if (bigEmoji) {
        return (
          <Text
            data-testid="big-emoji"
            fontSize={`${bigEmoji}px`}
            lineHeight="1.25"
            fontFamily={EMOJI_FONT}
            whiteSpace="pre-wrap"
          >
            {message.content.trim()}
          </Text>
        );
      }
      const url = firstUrl(message.content);
      return (
        <Box minW={0}>
          <Text whiteSpace="pre-wrap" overflowWrap="anywhere">
            {splitLinks(message.content).flatMap((part, p) =>
              part.url ? (
                // Shown as its host, opens the full URL (D354); the title
                // carries the full URL for anyone who wants to see it.
                <Link
                  key={`l${p}`}
                  href={part.url}
                  title={part.url}
                  isExternal
                  rel="noopener noreferrer"
                  color="accent"
                  textDecoration="underline"
                  data-testid="message-link"
                >
                  {part.text}
                </Link>
              ) : (
                splitMentions(part.text, mentionHandles).map((segment, i) =>
                  segment.handle ? (
                    <Text
                      as="span"
                      key={`${p}.${i}`}
                      fontWeight="semibold"
                      // Your own name stands out (D27), and so does @everyone,
                      // which includes you (D43).
                      bg={isForMe(segment.handle) ? 'yellow.200' : undefined}
                      color={isForMe(segment.handle) ? 'gray.900' : 'accent'}
                      borderRadius="sm"
                      px={isForMe(segment.handle) ? '2px' : 0}
                    >
                      {segment.text}
                    </Text>
                  ) : (
                    <Fragment key={`${p}.${i}`}>{segment.text}</Fragment>
                  )
                )
              )
            )}
          </Text>
          {url && (
            <LinkPreviewCard
              url={url}
              sealed={
                message.encryption
                  ? {
                      preview:
                        (message.e2ee?.envelope?.meta?.linkPreview as
                          | LinkPreview
                          | undefined) ?? null,
                    }
                  : undefined
              }
            />
          )}
        </Box>
      );
    }
  }
}

// Everything a bubble can ask the chat to do. One object so the list can pass
// it through without a prop per action.
export interface MessageActions {
  toggleStar: (message: ChatMessage) => void;
  saveLink: (message: ChatMessage) => void;
  saveDocument: (message: ChatMessage) => void;
  /** `index`: the item tapped in an album (M60, D219). */
  openImage: (message: ChatMessage, index?: number) => void;
  // M20 (docs/chat-essentials.md)
  reply: (message: ChatMessage) => void;
  edit: (message: ChatMessage) => void;
  remove: (message: ChatMessage) => void;
  react: (message: ChatMessage, emoji: string) => void;
  /** "＋" in the quick row: any emoji, from the picker (M69, D264). */
  pickReaction?: (message: ChatMessage) => void;
  /** Message info: who has seen it (M62, D227). */
  showInfo: (message: ChatMessage) => void;
  // M22 (docs/chat-round-two.md)
  togglePin: (message: ChatMessage) => void;
  forward: (message: ChatMessage) => void;
  jumpTo: (messageId: string, createdAt?: string) => void;
  /** Phones: the press-and-hold actions sheet. */
  openActions: (message: ChatMessage) => void;
  /** Report its sender (M58, D207). */
  report: (message: ChatMessage) => void;
  /** Group message → its sender's direct chat, quoted (M63, D235). */
  replyPrivately?: (message: ChatMessage) => void;
  /** Aashirwad: a one-tap blessing reply (D280). */
  bless?: (message: ChatMessage) => void;
  // M60 (D220): polls.
  votePoll: (message: ChatMessage, choices: number[] | null) => void;
  closePoll: (message: ChatMessage) => void;
  // M71 (D282–D288): gatherings.
  replyToGathering: (
    message: ChatMessage,
    reply: GatheringReply,
    lateMinutes?: number
  ) => void;
  takeBackGatheringReply: (message: ChatMessage) => void;
  arriveAtGathering: (message: ChatMessage) => void;
  ringGathering: (message: ChatMessage) => void;
  cancelGathering: (message: ChatMessage) => void;
  /** Everyone in the open chat, and whether you're a family root or admin. */
  gatheringPeople: { ids: string[]; isAdmin: boolean };
  nameOf: (userId: string) => string;
}

/** Mine on the right, everyone else's on the left (M68, D257). */
export type MessageSide = 'mine' | 'theirs';

interface MessageBubbleProps {
  message: ChatMessage;
  side: MessageSide;
  // First/last message of a run from the same sender (D258). Runs share one
  // avatar and one sender name, and the corners on the sender's side are
  // fuller only at the run's ends.
  isFirstInRun: boolean;
  isLastInRun: boolean;
  currentUserId: string | undefined;
  /** The chat's family: the sender's face opens through it (D292). */
  familyId: string;
  /** Everyone else in the chat, for read ticks (D25). */
  otherParticipantIds: string[];
  isGroup: boolean;
  highlighted: boolean;
  /** user id → handle for everyone in the chat, for @mentions (D27). */
  handleById: ReadonlyMap<string, string>;
  actions: MessageActions;
  onMediaLoad: () => void;
}

// Reactions grouped for display: emoji, how many, and whether one is mine.
function groupReactions(
  reactions: { userId: string; emoji: string }[],
  currentUserId: string | undefined
) {
  const groups = new Map<string, { count: number; mine: boolean }>();
  for (const { userId, emoji: raw } of reactions) {
    // One chip per emoji, whatever spelling an older app stored (D264).
    const emoji = canonicalEmoji(raw);
    const group = groups.get(emoji) ?? { count: 0, mine: false };
    group.count += 1;
    if (userId === currentUserId) group.mine = true;
    groups.set(emoji, group);
  }
  return [...groups.entries()].map(([emoji, group]) => ({ emoji, ...group }));
}

// Corner radii (D258): 20px everywhere, 8px on the sender's side where a run
// continues, so a run reads as one column of speech.
const CORNER = '20px';
const INNER_CORNER = '8px';

export function MessageBubble({
  message,
  side,
  isFirstInRun,
  isLastInRun,
  currentUserId,
  familyId,
  otherParticipantIds,
  isGroup,
  highlighted,
  handleById,
  actions,
  onMediaLoad,
}: MessageBubbleProps) {
  const mentionHandles = new Set(
    (message.mentions ?? [])
      .map((id) => handleById.get(id))
      .filter((handle): handle is string => Boolean(handle))
  );
  if (message.mentionsEveryone) mentionHandles.add(EVERYONE_MENTION);
  const myHandle = currentUserId ? handleById.get(currentUserId) : undefined;
  const toast = useToast();
  const t = useT();
  const desktop = useBreakpointValue({ base: false, md: true }) ?? false;
  const isOwn = side === 'mine';
  const blessingButton = useAtomValue(blessingButtonAtom);
  const deleted = Boolean(message.deletedAt);
  const locked = Boolean(message.e2ee?.locked);

  // Read state (D25): "Seen by N" in groups, ✓/✓✓ in direct chats.
  const readers = message.readBy.filter((id) => id !== currentUserId);
  const readByAll =
    otherParticipantIds.length > 0 &&
    otherParticipantIds.every((id) => readers.includes(id));
  const reactions = groupReactions(message.reactions ?? [], currentUserId);
  // A text of 1–3 emojis, with no quote or forwarded line, is drawn big
  // and without the bubble's background (D265).
  const plainText =
    !deleted &&
    !locked &&
    message.type === 'text' &&
    !message.replyTo &&
    !message.forwarded;
  const bigEmoji = useMemo(
    () => (plainText ? bigEmojiSize(message.content) : null),
    [plainText, message.content]
  );

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      toast({
        title: t('chat.actions.copied'),
        status: 'success',
        duration: 1500,
      });
    } catch {
      toast({ title: t('chat.actions.couldNotCopy'), status: 'error' });
    }
  };

  const items = messageActionItems({
    message,
    isOwn,
    isGroup,
    otherParticipantCount: otherParticipantIds.length,
    actions,
    copyText: () => void copyText(),
  });

  const time = new Date(message.createdAt).toLocaleTimeString(appLocale(), {
    hour: 'numeric',
    minute: '2-digit',
  });
  const memberLabels = useAtomValue(memberLabelsAtom);
  const nameOf = useAtomValue(memberNameAtom);
  // Sealed: from the bound sender and the members as they are now (D327).
  const senderShown = message.sender
    ? shownSenderName(
        {
          senderId: message.sender.id,
          name: message.sender.displayName,
          e2ee: message.e2ee,
          deleted: message.sender.deleted,
        },
        nameOf
      )
    : undefined;
  const senderName = isOwn
    ? t('common.you')
    : ((message.sender && memberLabels[message.sender.id]) ??
      senderShown ??
      t('chat.unknown'));

  const actionButton = desktop ? (
    // Opens away from the bubble: the ⋯ is left of mine, right of theirs.
    <Menu placement={isOwn ? 'bottom-start' : 'bottom-end'} isLazy>
      {({ onClose }) => (
        <>
          <MenuButton
            as={IconButton}
            aria-label={t('chat.actions.label')}
            icon={<MoreHorizontal size={18} />}
            size="sm"
            variant="ghost"
            colorScheme="gray"
            color="muted"
            className="message-actions"
          />
          <MenuList>
            {!deleted && message.type !== 'system' && (
              <Box px={1} minW="324px">
                <QuickReactions
                  onReact={(emoji) => {
                    actions.react(message, emoji);
                    onClose();
                  }}
                  onMore={() => {
                    onClose();
                    actions.pickReaction?.(message);
                  }}
                />
              </Box>
            )}
            {!deleted && <MenuDivider my={1} />}
            {items.map((item) => (
              <MenuItem
                key={item.key}
                icon={item.icon as React.ReactElement}
                color={item.danger ? 'danger' : undefined}
                onClick={item.onSelect}
              >
                <HStack justify="space-between" w="full">
                  <span>{item.label}</span>
                  {item.detail && (
                    <Text as="span" color="muted" fontSize="sm">
                      {item.detail}
                    </Text>
                  )}
                </HStack>
              </MenuItem>
            ))}
          </MenuList>
        </>
      )}
    </Menu>
  ) : (
    <IconButton
      aria-label={t('chat.actions.label')}
      icon={<MoreHorizontal size={18} />}
      size="sm"
      variant="ghost"
      colorScheme="gray"
      color="muted"
      className="message-actions"
      onClick={() => actions.openActions(message)}
    />
  );

  // The sender's name is drawn only in groups, above the first bubble of
  // someone else's run (D257); everywhere else it is read, not seen (D260).
  const showsName = isGroup && !isOwn && isFirstInRun;
  const avatarColumn = isGroup && !isOwn;
  const seen = readers.length > 0;
  const receiptLabel = isGroup
    ? seen
      ? t('chat.message.seenBy', { n: readers.length })
      : t('chat.message.sent')
    : seen
      ? t('chat.message.seen')
      : t('chat.message.sent');

  // Logical corners so a right-to-left language mirrors the whole row (D260).
  const outerTop = isFirstInRun ? CORNER : INNER_CORNER;
  const outerBottom = isLastInRun ? CORNER : INNER_CORNER;
  const corners = isOwn
    ? {
        borderStartStartRadius: CORNER,
        borderEndStartRadius: CORNER,
        borderStartEndRadius: outerTop,
        borderEndEndRadius: outerBottom,
      }
    : {
        borderStartStartRadius: outerTop,
        borderEndStartRadius: outerBottom,
        borderStartEndRadius: CORNER,
        borderEndEndRadius: CORNER,
      };
  const muted = deleted || locked;

  // Always after the bubble in the DOM, so tab and reading order never
  // depend on the side (D260); `order` moves it before an own bubble.
  // Aashirwad (D280): for people who turned the button on in Settings.
  const blessButton =
    blessingButton && actions.bless && canBless(message, isOwn) ? (
      <IconButton
        aria-label={t('chat.bless.quick', {
          name: senderShown ?? '',
        })}
        icon={
          <Box as="span" fontSize="20px" fontFamily={EMOJI_FONT}>
            🙏
          </Box>
        }
        size="sm"
        h="40px"
        minW="40px"
        variant="ghost"
        colorScheme="gray"
        borderRadius="full"
        data-testid="bless-button"
        onClick={() => actions.bless?.(message)}
      />
    ) : null;
  const dots = (
    <Box flexShrink={0} alignSelf="center" order={isOwn ? -1 : undefined}>
      {actionButton}
      {blessButton}
    </Box>
  );

  // Mine on the right, theirs on the left (M68, D257–D260). Runs are 2px
  // apart, 12px between runs (D258).
  return (
    <Flex
      id={`message-${message.id}`}
      data-side={side}
      data-run-start={isFirstInRun || undefined}
      data-run-end={isLastInRun || undefined}
      mt={isFirstInRun ? '12px' : '2px'}
      gap="4px"
      justify={isOwn ? 'flex-end' : 'flex-start'}
      align="flex-start"
      role="group"
      sx={{
        // With a mouse the "…" stays out of the way until hovered or focused.
        // On touch screens it's always there: it opens the actions, and press
        // and hold is the phone's own text selection (D140). It stays in the
        // page, so keyboard and screen-reader users reach every action.
        '@media (hover: hover)': {
          '.message-actions': { opacity: 0 },
          '&:hover .message-actions, .message-actions:focus-visible, .message-actions[aria-expanded=true]':
            { opacity: 1 },
        },
      }}
    >
      {/* Avatar column (groups, theirs): reserved on every row so a run's
          bubbles line up; drawn once per run (D257). */}
      {avatarColumn && (
        <Box w="32px" flexShrink={0} me="4px">
          {isFirstInRun && (
            <Avatar
              w="32px"
              h="32px"
              fontSize="13px"
              // The name is drawn beside it; don't read it twice.
              aria-hidden="true"
              name={senderShown}
              src={message.sender?.avatarUrl ?? undefined}
              ownerId={message.sender?.id}
              familyId={familyId}
            />
          )}
        </Box>
      )}

      <Flex
        direction="column"
        align={isOwn ? 'flex-end' : 'flex-start'}
        maxW="80%"
        minW={0}
      >
        {showsName && (
          <Text
            as="span"
            data-testid="sender-name"
            fontWeight={700}
            fontSize="13px"
            color="muted"
            noOfLines={1}
            mb="2px"
            ms="12px"
          >
            {senderName}
          </Text>
        )}

        <Box
          data-testid="bubble"
          data-big-emoji={bigEmoji ? 'true' : undefined}
          minW="48px"
          maxW="100%"
          px={bigEmoji ? '4px' : '12px'}
          py={bigEmoji ? '2px' : '8px'}
          bg={
            bigEmoji
              ? 'transparent'
              : muted
                ? 'surface.subtle'
                : isOwn
                  ? 'accent.tint'
                  : 'surface'
          }
          borderWidth={isOwn || bigEmoji ? 0 : '1px'}
          borderColor="line"
          boxShadow={
            highlighted ? '0 0 0 3px var(--chakra-colors-saffron)' : undefined
          }
          transition="box-shadow 0.3s"
          sx={corners}
        >
          {/* The sender, for screen readers, when the name isn't drawn
              (D260); outside the selectable text so copying never picks it
              up (D140). */}
          {!showsName && (
            <VisuallyHidden as="span" sx={{ userSelect: 'none' }}>
              {senderName}:{' '}
            </VisuallyHidden>
          )}

          {message.forwarded && !deleted && (
            <HStack
              spacing={1}
              fontSize="13px"
              fontStyle="italic"
              color="muted"
              mb="2px"
            >
              <Forward size={13} aria-hidden="true" />
              <Text as="span">{t('chat.message.forwarded')}</Text>
            </HStack>
          )}

          {message.replyTo && !deleted && (
            <Box
              as="button"
              type="button"
              display="block"
              w="full"
              textAlign="start"
              bg={isOwn ? 'surface' : 'surface.subtle'}
              borderInlineStartWidth="3px"
              borderInlineStartColor="accent"
              borderRadius="12px"
              px={3}
              py={2}
              mb="6px"
              onClick={() =>
                actions.jumpTo(message.replyTo!.id, message.replyTo!.createdAt)
              }
            >
              <Text
                fontSize="13px"
                fontWeight={700}
                color="accent"
                noOfLines={1}
              >
                {shownSenderName(
                  {
                    senderId: message.replyTo.senderId,
                    name: message.replyTo.senderName,
                    e2ee: message.replyTo.e2ee,
                    deleted: message.replyTo.senderDeleted,
                  },
                  nameOf
                )}
              </Text>
              <Text fontSize="14px" color="ink.body" noOfLines={2}>
                {message.replyTo.deleted
                  ? t('chat.message.replyDeleted')
                  : shortenLinks(message.replyTo.preview)}
              </Text>
              {isUnbound(message.replyTo) && <UnboundMark />}
            </Box>
          )}

          {deleted ? (
            <HStack
              spacing={1.5}
              fontStyle="italic"
              fontSize="15px"
              color="muted"
            >
              <Ban size={15} aria-hidden="true" />
              <Text>{t('chat.message.deleted')}</Text>
            </HStack>
          ) : (
            <Box
              fontSize="17px"
              lineHeight="25px"
              data-selectable="true"
              textAlign={bigEmoji && isOwn ? 'end' : undefined}
            >
              <MessageBody
                message={message}
                isOwn={isOwn}
                mentionHandles={mentionHandles}
                myHandle={myHandle}
                onOpenImage={actions.openImage}
                onMediaLoad={onMediaLoad}
                currentUserId={currentUserId}
                bigEmoji={bigEmoji}
                pollActions={{
                  vote: actions.votePoll,
                  close: actions.closePoll,
                  nameOf: actions.nameOf,
                }}
                gatheringActions={{
                  reply: actions.replyToGathering,
                  takeBack: actions.takeBackGatheringReply,
                  arrive: actions.arriveAtGathering,
                  ring: actions.ringGathering,
                  cancel: actions.cancelGathering,
                  nameOf: actions.nameOf,
                }}
                gatheringPeople={actions.gatheringPeople}
              />
            </Box>
          )}

          {/* An older (v1) envelope: opened, but its sender, type and edit
              aren't bound, so the app doesn't vouch for them (M76, D328). */}
          {isUnbound(message) && !deleted && (
            <UnboundMark mt="2px" textAlign={isOwn ? 'end' : 'start'} />
          )}

          {/* Time in the bubble's bottom corner on the sender's side, with
              the expiring timer (D238), pin, star, "edited" and, on mine,
              the receipt glyph (D258). */}
          <HStack
            spacing="6px"
            mt="2px"
            fontSize="12px"
            lineHeight="16px"
            color="muted"
            justify={isOwn ? 'flex-end' : 'flex-start'}
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {message.expiresAt && !deleted && (
              <HStack
                as="span"
                spacing={1}
                data-testid="expiry-timer"
                role="img"
                aria-label={t('chat.message.disappears', {
                  time: expiryLabel(message.expiresAt),
                })}
              >
                <Timer size={12} aria-hidden="true" />
              </HStack>
            )}
            {message.pinnedAt && !deleted && (
              <HStack
                as="span"
                spacing={1}
                aria-label={t('chat.message.pinned')}
              >
                <Pin size={12} aria-hidden="true" />
                <span>{t('chat.message.pinned')}</span>
              </HStack>
            )}
            {message.starredByMe && !deleted && (
              <HStack
                as="span"
                spacing={1}
                aria-label={t('chat.viewer.starred')}
              >
                <Star size={12} fill="currentColor" aria-hidden="true" />
              </HStack>
            )}
            {message.editedAt && !deleted && (
              <Text as="span" fontStyle="italic">
                {t('chat.message.edited')}
              </Text>
            )}
            <Text as="span">{time}</Text>
            {isOwn && !deleted && (
              <Box
                as="span"
                role="img"
                aria-label={receiptLabel}
                title={receiptLabel}
                data-testid="receipt"
                data-state={
                  readByAll ? 'seen-all' : seen ? 'seen-some' : 'sent'
                }
                display="inline-flex"
                alignItems="center"
                color={readByAll ? 'accent' : 'muted'}
              >
                {seen ? (
                  <CheckCheck size={14} aria-hidden="true" />
                ) : (
                  <Check size={14} aria-hidden="true" />
                )}
                {/* Some but not all: the count, so it isn't colour alone. */}
                {isGroup && seen && !readByAll && (
                  <Text as="span" ms="2px" aria-hidden="true">
                    {readers.length}
                  </Text>
                )}
              </Box>
            )}
          </HStack>
        </Box>

        {/* Reactions hang off the bubble's bottom edge, on its outer side
            (D259). */}
        {reactions.length > 0 && (
          <HStack
            spacing={1}
            mt={bigEmoji ? '0' : '-10px'}
            mx="8px"
            flexWrap="wrap"
            position="relative"
            zIndex={1}
            justify={isOwn ? 'flex-end' : 'flex-start'}
          >
            {reactions.map(({ emoji, count, mine }) => (
              // D264: a 20 px emoji with its count beside it, on a 28 px chip.
              <Button
                key={emoji}
                data-testid="reaction-chip"
                h="28px"
                minH="28px"
                minW="28px"
                px="8px"
                variant="outline"
                colorScheme="gray"
                borderRadius="full"
                borderWidth="1px"
                borderColor={mine ? 'accent' : 'line'}
                bg={mine ? 'accent.tint' : 'surface'}
                boxShadow="0 1px 2px rgba(0, 0, 0, 0.08)"
                _hover={{ opacity: 0.85 }}
                fontWeight="normal"
                aria-pressed={mine}
                aria-label={`${emoji} ${count}${mine ? t('chat.message.includingYou') : ''}`}
                onClick={() => actions.react(message, emoji)}
              >
                <Box
                  as="span"
                  fontSize="20px"
                  lineHeight="1"
                  fontFamily={EMOJI_FONT}
                  aria-hidden="true"
                >
                  {emoji}
                </Box>
                {count > 1 && (
                  <Text
                    as="span"
                    ms="4px"
                    fontSize="13px"
                    fontWeight={600}
                    color="ink.body"
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                    aria-hidden="true"
                  >
                    {count}
                  </Text>
                )}
              </Button>
            ))}
          </HStack>
        )}
      </Flex>

      {dots}
    </Flex>
  );
}
